import { useMemo, useState } from "react";
import { LOCATIONS, LOCATION_LABEL, STAGES, STAGE_LABEL, type Location, type Stage } from "@h2lca/core";
import { Button, SegmentedControl, SelectField, TextField } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { technologiesFor, templatesFor, useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";
import { EquipmentIcon } from "./EquipmentIcon";

type Tab = "palette" | "custom";
type StageFilter = Stage | "all";

const STAGE_OPTIONS: readonly { value: StageFilter; label: string }[] = [
  { value: "all", label: "전체" },
  ...STAGES.map((s) => ({ value: s, label: STAGE_LABEL[s] })),
];

/** 오른쪽 설비 팔레트 , 분류,대상기술로 거른 기본 설비와 기타(사용자 정의) 설비 */
export function EquipmentPalette() {
  const data = useMaster((s) => s.data);
  const productionTechId = useWorkshop((s) => s.scenario?.basis.productionTechId ?? "");
  const addFromTemplate = useWorkshop((s) => s.addFromTemplate);
  const addCustom = useWorkshop((s) => s.addCustom);
  const [tab, setTab] = useState<Tab>("palette");
  const [stage, setStage] = useState<StageFilter>("all");
  const [techByStage, setTechByStage] = useState<Partial<Record<StageFilter, string>>>({});

  // 분류별 대상기술 목록. 전체,생산은 시나리오의 생산방식이 기본값
  const techs = useMemo(() => (stage === "all" ? data.technologies.filter((t) => t.active).sort((a, b) => a.stage.localeCompare(b.stage) || a.order - b.order) : technologiesFor(data, stage)), [data, stage]);
  const defaultTech = stage === "all" || stage === "production" ? productionTechId : (techs.find((t) => t.status === "ready")?.id ?? "");
  const techId = techByStage[stage] ?? defaultTech;
  const templates = useMemo(() => templatesFor(data, stage, techId || undefined), [data, stage, techId]);
  const techName = data.technologies.find((t) => t.id === techId)?.name;

  const [name, setName] = useState("");
  const [cStage, setCStage] = useState<Stage>("auxiliary");
  const [cLoc, setCLoc] = useState<Location>("onsite");

  return (
    <div className="flex flex-col gap-content rounded-panel border border-line-soft bg-panel p-content">
      <SegmentedControl
        label="설비 추가 방식"
        value={tab}
        options={[
          { value: "palette", label: "설비 팔레트" },
          { value: "custom", label: "기타(사용자 정의)" },
        ]}
        onChange={setTab}
      />

      {tab === "palette" ? (
        <>
          <div>
            <p className="control-label">설비분류</p>
            <SegmentedControl label="설비분류" value={stage} options={STAGE_OPTIONS} onChange={setStage} />
          </div>
          <SelectField label="대상기술" value={techId} onChange={(e) => setTechByStage((m) => ({ ...m, [stage]: e.target.value }))} hint={stage === "all" ? "기술과 무관한 공통 설비(압축기, 탱크)는 늘 함께 보입니다." : undefined}>
            <option value="">전체 기술</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id} disabled={t.status === "placeholder"}>
                {stage === "all" ? `[${STAGE_LABEL[t.stage]}] ` : ""}
                {t.name}
                {t.status === "placeholder" ? " (협의 후 제공)" : ""}
              </option>
            ))}
          </SelectField>
          <p className="text-footnote text-ink-subtle">
            {techName ? `현재: ${techName}` : "모든 기술의 설비"}. 설비를 선택하면 공정의 마지막 순서에 추가됩니다.
          </p>
          <ul className="flex flex-col gap-inline">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center gap-label">
                <button type="button" className="palette-item" onClick={() => addFromTemplate(t.id)}>
                  <span className="palette-icon">
                    <EquipmentIcon icon={t.icon} className="size-icon" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{t.name}</span>
                    <span className="block text-micro text-ink-subtle">
                      {STAGE_LABEL[t.stage]}, {LOCATION_LABEL[t.location]}
                      {t.h2.role === "producer" ? ", 수소 생산" : t.h2.role === "passthrough" ? ", 수소 통과" : ""}
                    </span>
                  </span>
                </button>
                {(t.description || t.help) && (
                  <InfoTip label={`${t.name} 설명`}>
                    {t.description}
                    {t.help && (
                      <>
                        <br />
                        {t.help}
                      </>
                    )}
                    {t.inputs.length > 0 && (
                      <>
                        <br />
                        <span className="text-ink-subtle">입력 항목: {t.inputs.map((i) => i.label).join(", ")}</span>
                      </>
                    )}
                  </InfoTip>
                )}
              </li>
            ))}
            {templates.length === 0 && <li className="text-footnote text-ink-subtle">이 조건에 맞는 기본 설비가 없습니다. 협의 후 제공되는 기술이거나 기타(사용자 정의)로 추가하세요.</li>}
          </ul>
        </>
      ) : (
        <form
          className="flex flex-col gap-content"
          onSubmit={(e) => {
            e.preventDefault();
            addCustom(cStage, cLoc, name.trim() || undefined);
            setName("");
          }}
        >
          <p className="text-footnote text-ink-subtle">프로그램이 지원하는 입력항목과 단위 안에서 설비를 직접 정의합니다. 활동자료, 사용량, 배출계수는 운전자료 입력 단계에서 채웁니다.</p>
          <TextField label="설비명" value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 탈수소 반응기" />
          <SelectField label="설비분류" value={cStage} onChange={(e) => setCStage(e.target.value as Stage)}>
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </SelectField>
          <SelectField label="설비 위치" value={cLoc} onChange={(e) => setCLoc(e.target.value as Location)} hint="인증제 모의산정은 원료 조달과 생산시설 내부만 산정범위에 넣습니다.">
            {LOCATIONS.map((l) => (
              <option key={l} value={l}>
                {LOCATION_LABEL[l]}
              </option>
            ))}
          </SelectField>
          <Button type="submit" variant="primary" fullWidth>
            사용자 정의 설비 추가
          </Button>
        </form>
      )}
    </div>
  );
}
